import React, { useState, useEffect } from 'react';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';
import { View } from 'react-native';
import PlacesAutocompleteComponent from '../MapAutoComplete/AutoComplete';

import { Position } from '@typeRoots/common/UI';
import { styles } from './map.styles';
import { Address, AddressComponent } from 'src/types/common/address';
import { RootState } from 'src/store/store';
import { useSelector } from 'react-redux';
import { mapFetchApi } from 'src/helpers/addressHelper';

export interface CustomMapProps {
  initialPosition: Position;
  setMapAddress: (address: Address) => void;
  mapAddress: Address;
  setFormattedAddress: (address: { line1: string; line2: string }) => void;
  testID?: string;
}

const CustomMap: React.FC<CustomMapProps> = ({
  initialPosition,
  setMapAddress,
  mapAddress,
  setFormattedAddress,
  testID,
}) => {
  const mapPosition = { lat: mapAddress.latitude, lng: mapAddress.longitude };
  const [markerPosition, setMarkerPosition] = useState(mapPosition);
  const [address, setAddress] = useState<Address>(mapAddress);
  const [mapZoom, setMapZoom] = useState(18);
  const [mapCenter, setMapCenter] = useState(mapPosition);
  const token = useSelector((state: RootState) => state.users.userToken); // Get the token from Redux store
  const tpJwt = useSelector((state: RootState) => state.UI.tpJwtToken); // Get the token from Redux store

  useEffect(() => {
    const position = { lat: mapAddress.latitude, lng: mapAddress.longitude };
    //Sync marker position with initial position when the map is first loaded
    setMarkerPosition(position);
    fetchAddress(position);
    setAddress(mapAddress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapAddress]);
  useEffect(() => {
    //Sync marker position with initial position when the map is first loaded
    setMarkerPosition(mapPosition);
    fetchAddress(mapPosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    //Sync marker position with initial position when the map is first loaded
    if (address.latitude !== 0 && address.longitude !== 0) {
      const position = { lat: address.latitude, lng: address.longitude };
      setMarkerPosition(position);
      setMapCenter(position);
      setMapZoom(18);
      setMapAddress(address);
      fetchAddress(position);
    }
  }, [address]);

  const formatAddress = (components: AddressComponent[]): { line1: string; line2: string } => {
    let streetNumber = '';
    let route = '';
    let locality = '';
    let administrativeArea = '';
    let postalCode = '';
    let county = '';

    components.forEach((component) => {
      //string address = line1, either line1 or the formatted address from detailed component
      //city = locality = component.types.includes('locality')
      //state =        administrativeArea = component.short_name;administrative_area_level_1
      //zipcode =         postalCode = component.long_name;postal_code
      //county = "administrative_area_level_2"
      if (component.types.includes('street_number')) {
        streetNumber = component.long_name;
      } else if (component.types.includes('route')) {
        route = component.long_name;
      } else if (component.types.includes('locality')) {
        locality = component.long_name;
      } else if (component.types.includes('administrative_area_level_1')) {
        administrativeArea = component.short_name;
      } else if (component.types.includes('administrative_area_level_2')) {
        county = component.short_name;
      } else if (component.types.includes('postal_code')) {
        postalCode = component.long_name;
      }
    });
    const line1 = `${streetNumber} ${route}`.trim(); // First line: street address
    const line2 = `${locality}, ${administrativeArea} ${postalCode}`.trim(); // Second line: city, state, ZIP
    setAddress({
      ...address,
      street: line1,
      city: locality,
      state: administrativeArea,
      zipCode: postalCode,
      county: county,
    });
    return { line1, line2 };
  };
  // Fetch address from Geocoding API
  //const apiKey = process.env.REACT_APP_SECRET_MAP_API_KEY
  const fetchAddress = async (position: Position) => {
    if (mapAddress.latitude === position.lat && mapAddress.longitude === position.lng) {
      return;
    }
    try {
      const url = token
        ? `${process.env.REACT_APP_API_BASE_URL}/maps/geocode/latlng?lat=${position.lat}&lng=${position.lng}`
        : `${process.env.REACT_APP_API_BASE_URL}/auth/latlng?lat=${position.lat}&lng=${position.lng}&tpJwt=${tpJwt}`;

      const response = await mapFetchApi(token, url);

      const data = await response.json();
      if (data.results.length > 0) {
        setFormattedAddress(
          formatAddress(data.results[0]?.address_components as AddressComponent[]),
        );
        //setAddress(data.results[0].formatted_address);
        //setMapAddress(data.results[0].formatted_address)
      } else {
        console.error('No results found');
      }
    } catch (error) {
      console.error('Error fetching address:', error);
    }
  };

  const handleMarkerDragEnd = (newPosition: Position) => {
    setMarkerPosition(newPosition);
    fetchAddress(newPosition);
  };
  /*
  Commented this fucntionality so that we can use it in future if needed.
  const handleMapDragEnd = () => {
    fetchAddress(markerPosition);
  };*/
  const handleMapCenterChanged = (center: Position) => {
    if (center) {
      setMapCenter(center);
    }
  };

  const handleZoomChange = (zoom: number) => {
    setMapZoom(zoom);
  };
  return (
    <View style={styles.container}>
      <APIProvider
        apiKey={process.env.REACT_APP_SECRET_MAP_API_KEY as string}
        libraries={['places']} // Places library will be loaded
        onLoad={() => console.log('Google Maps API loaded')}
        onError={(error) => console.error('Error loading Google Maps API:', error)}>
        <PlacesAutocompleteComponent
          mapAddress={address}
          setMapAddress={setAddress}
          testID={testID}
        />
        <Map
          defaultCenter={initialPosition}
          defaultZoom={18}
          zoom={mapZoom}
          onZoomChanged={(e) => handleZoomChange(e.detail.zoom)}
          /**Commented this fucntionality so that we can use it in future if needed. */
          center={mapCenter}
          style={styles.map}
          onCenterChanged={(e) => {
            handleMapCenterChanged(e.detail.center);
          }}
          /*onClick={(e) => {
            let newPosition = {
              lat: e.detail.latLng?.lat,
              lng: e.detail.latLng?.lng,
            };
            handleMarkerDragEnd(newPosition);
          }}*/
          //onDragend={handleMapDragEnd}
          disableDefaultUI={false}>
          <Marker
            position={markerPosition}
            draggable={false}
            onDragEnd={(e) => {
              let newPosition = {
                lat: e.latLng?.lat(),
                lng: e.latLng?.lng(),
              };
              handleMarkerDragEnd(newPosition as Position);
            }}
          />
        </Map>
      </APIProvider>
    </View>
  );
};

export default CustomMap;
